import { CharacterPortrait, DialogueItem, Scenario, Scene, SceneElement, StageCharacter } from '../types';
import { resolveStageFromSpine } from './stage-runtime';
import {
  normalizeEmotionKey,
  normalizeEmotionsRecord,
} from '../components/scenario-editor/emotion-presets';

export type CharacterPlacement = 'gauche' | 'centre' | 'droite';

export const PLACEMENT_X: Record<CharacterPlacement, number> = {
  gauche: 22,
  centre: 50,
  droite: 78,
};

export function placementFromX(x: number): CharacterPlacement {
  if (x <= 35) return 'gauche';
  if (x >= 65) return 'droite';
  return 'centre';
}

export function characterElementId(characterId: string): string {
  return `${characterId}_char`;
}

/** Resolve a show/hide target or element id to a metadata character id. */
export function parseCharacterIdFromTarget(target: string): string | null {
  if (!target) return null;
  if (target.endsWith('_char')) return target.slice(0, -'_char'.length);
  return target;
}

export function buildMoodsFromPortrait(char: CharacterPortrait): Record<string, string> {
  const moods = normalizeEmotionsRecord(char.emotions);
  if (char.defaultImage && !moods.neutre) {
    moods.neutre = char.defaultImage;
  }
  return moods;
}

export function defaultMoodFor(char: CharacterPortrait, emotion?: string): string {
  const moods = buildMoodsFromPortrait(char);
  if (emotion) {
    if (moods[emotion]) return emotion;
    const normalized = normalizeEmotionKey(emotion);
    if (normalized && moods[normalized]) return normalized;
  }
  if (moods.neutre) return 'neutre';
  const keys = Object.keys(moods);
  return keys[0] ?? 'neutre';
}

export function resolveCharacterPortrait(
  characters: CharacterPortrait[] | undefined,
  characterIdOrTarget: string
): CharacterPortrait | undefined {
  if (!characters?.length) return undefined;
  const id = parseCharacterIdFromTarget(characterIdOrTarget);
  if (!id) return undefined;
  return characters.find((c) => c.id === id);
}

export function resolveMoodImage(char: CharacterPortrait, mood: string): string | undefined {
  const moods = buildMoodsFromPortrait(char);
  return moods[mood] ?? moods[defaultMoodFor(char, mood)] ?? char.defaultImage;
}

export function collectSceneCharacterIds(scene: Scene): Set<string> {
  const ids = new Set<string>();

  const add = (raw?: string) => {
    if (!raw) return;
    const id = parseCharacterIdFromTarget(raw);
    if (id) ids.add(id);
  };

  if (scene.type === 'dialogue') {
    add(scene.characterId);
    add(scene.characterMood?.elementId);
  } else if (scene.type === 'command') {
    if (
      scene.command === 'showElement' ||
      scene.command === 'hideElement' ||
      scene.command === 'animateElement'
    ) {
      add(scene.target);
    }
  } else if (scene.type === 'choice') {
    for (const opt of scene.options ?? []) {
      for (const mc of opt.moodChanges ?? []) {
        add(mc.elementId);
      }
    }
  }

  return ids;
}

export function getDialoguePlacement(
  dialogue: DialogueItem,
  sceneElements?: SceneElement[]
): CharacterPlacement {
  if (dialogue.placement) return dialogue.placement;
  if (!dialogue.characterId) return 'centre';

  const elementId =
    dialogue.characterMood?.elementId ?? characterElementId(dialogue.characterId);
  const legacy = sceneElements?.find(
    (e) => e.type === 'character' && (e.id === elementId || e.id === dialogue.characterId)
  );
  if (legacy?.position?.x != null) return placementFromX(legacy.position.x);
  return 'centre';
}

export function buildVirtualCharacterElement(
  char: CharacterPortrait,
  placement: CharacterPlacement,
  elementId = characterElementId(char.id),
  emotion?: string
): SceneElement {
  const moods = buildMoodsFromPortrait(char);
  const defaultMood = defaultMoodFor(char, emotion);
  return {
    id: elementId,
    type: 'character',
    position: {
      x: PLACEMENT_X[placement],
      y: 50,
      width: 28,
      height: 75,
    },
    moods,
    defaultMood,
    image: moods[defaultMood] ?? char.defaultImage,
  };
}

/** Build on-screen character sprites from metadata.characters (no scene.elements needed). */
export function buildSceneCharacterElements(
  scene: Scene,
  characters: CharacterPortrait[]
): SceneElement[] {
  const ids = collectSceneCharacterIds(scene);
  const legacyElements = (scene.elements ?? []).filter((e) => e.type === 'character');
  const placements = new Map<string, CharacterPlacement>();

  if (scene.type === 'dialogue' && scene.characterId && !scene.isInternal) {
    placements.set(scene.characterId, getDialoguePlacement(scene, scene.elements));
  }

  return [...ids]
    .map((id) => {
      const char = characters.find((c) => c.id === id);
      if (!char) return null;
      const placement =
        placements.get(id) ??
        (() => {
          const legacy = legacyElements.find(
            (e) => e.id === characterElementId(id) || parseCharacterIdFromTarget(e.id) === id
          );
          return legacy?.position?.x != null ? placementFromX(legacy.position.x) : 'centre';
        })();
      return buildVirtualCharacterElement(char, placement);
    })
    .filter((el): el is SceneElement => el !== null);
}

export function buildSceneCharacterElementsFromStage(
  stage: StageCharacter[],
  characters: CharacterPortrait[]
): SceneElement[] {
  return stage
    .map((sc) => {
      const char = characters.find((c) => c.id === sc.characterId);
      if (!char) return null;
      const placement = sc.placement ?? 'centre';
      return buildVirtualCharacterElement(
        char,
        placement,
        characterElementId(sc.characterId),
        sc.emotion
      );
    })
    .filter((el): el is SceneElement => el !== null);
}

export function mergeSceneElementsForRender(
  scene: Scene,
  characters: CharacterPortrait[] | undefined,
  scenario?: Scenario
): SceneElement[] {
  const base = scene.elements ?? [];
  const nonCharacters = base.filter((e) => e.type !== 'character');
  if (!characters?.length) return base;

  const stage = scenario
    ? resolveStageFromSpine(scenario, scene.id)
    : null;

  const virtualCharacters = stage
    ? buildSceneCharacterElementsFromStage(stage, characters)
    : buildSceneCharacterElements(scene, characters);

  return [...nonCharacters, ...virtualCharacters];
}

export function normalizeCharacterCommandTarget(target: string): string {
  const id = parseCharacterIdFromTarget(target);
  return id ? characterElementId(id) : target;
}

export function resolveActiveCharacterElementId(
  dialogue: DialogueItem | null | undefined
): string | null {
  if (!dialogue || dialogue.type !== 'dialogue' || dialogue.isInternal) return null;
  if (dialogue.characterMood?.elementId) return dialogue.characterMood.elementId;
  if (dialogue.characterId) return characterElementId(dialogue.characterId);
  return null;
}

export function resolveDialogueMood(dialogue: DialogueItem): string | undefined {
  if (dialogue.isInternal) return undefined;
  return dialogue.emotion ?? dialogue.characterMood?.mood;
}
